import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  ButtonComp,
  CardComp,
} from "../../../components";
import { PageTitle } from "../modules/PageHeaders";
import {
  useGetEventSettingsQuery,
  useUpdateEventSettingsMutation,
} from "../../../store/admin/eventSettingsSlice";
import { toast } from "react-hot-toast";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";

const EventSettings = () => {
  const { data: settings, isLoading } = useGetEventSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] =
    useUpdateEventSettingsMutation();

  const validationSchema = Yup.object({
    channels: Yup.array().of(Yup.string().oneOf(['email', 'sms', 'push'])).min(1, "At least one channel must be selected"),
  });

  const handleSubmit = async (values) => {
    try {
      const response = await updateSettings(values).unwrap();
      if (response?.status === true) {
        toast.success(response?.message || "Event settings updated successfully");
      } else {
        toast.error(response?.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error updating event settings:", error);
      toast.error(error?.data?.message || "An error occurred while updating settings");
    }
  };

  if (isLoading) {
    return (
      <section className="px-3 px-lg-5 min-vh-100 pb-5 mb-5">
        <PageTitle title="Event Notification Settings" />
        <CardComp cardClassName="mt-4">
          <TableSkeletonLoader rows={5} columns={1} />
        </CardComp>
      </section>
    );
  }

  const initialValues = {
    channels: settings?.channels ?? ["email", "sms", "push"], // Default: all channels enabled
  };

  return (
    <section className="px-3 px-lg-5 min-vh-100 pb-5 mb-5">
      <div className="mt-1">
        <PageTitle title="Event Notification Settings" />
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form className="mt-4">
            <div className="space-y-4">
              {/* Communication Channels */}
              <CardComp cardClassName="rounded-4 shadow3 border-0">
                <h5 className="fw-bold text-black2 mb-3">Communication Channels</h5>
                <p className="text-blacklight mb-3 small">
                  Select how event notifications should be delivered. All channels are enabled by default.
                </p>
                <div className="space-y-3">
                  <div className="d-flex align-items-center justify-content-between border-bottom pb-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="channel_email"
                        checked={values.channels.includes("email")}
                        onChange={(e) => {
                          const newChannels = e.target.checked
                            ? [...values.channels, "email"]
                            : values.channels.filter((c) => c !== "email");
                          setFieldValue("channels", newChannels);
                        }}
                      />
                      <label className="form-check-label pointer fw-bold" htmlFor="channel_email">
                        Email
                      </label>
                    </div>
                    <span 
                      className="px-3 py-1 rounded-pill" 
                      style={{ background: "#dafceb", color: "#19bc31", fontSize: "12px", fontWeight: "600" }}
                    >
                      Free
                    </span>
                  </div>

                  <div className="d-flex align-items-center justify-content-between border-bottom pb-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="channel_push"
                        checked={values.channels.includes("push")}
                        onChange={(e) => {
                          const newChannels = e.target.checked
                            ? [...values.channels, "push"]
                            : values.channels.filter((c) => c !== "push");
                          setFieldValue("channels", newChannels);
                        }}
                      />
                      <label className="form-check-label pointer fw-bold" htmlFor="channel_push">
                        Push/In-App Notification
                      </label>
                    </div>
                    <span 
                      className="px-3 py-1 rounded-pill" 
                      style={{ background: "#dafceb", color: "#19bc31", fontSize: "12px", fontWeight: "600" }}
                    >
                      Free
                    </span>
                  </div>

                  <div className="d-flex align-items-center justify-content-between">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="channel_sms"
                        checked={values.channels.includes("sms")}
                        onChange={(e) => {
                          const newChannels = e.target.checked
                            ? [...values.channels, "sms"]
                            : values.channels.filter((c) => c !== "sms");
                          setFieldValue("channels", newChannels);
                        }}
                      />
                      <label className="form-check-label pointer fw-bold" htmlFor="channel_sms">
                        SMS
                      </label>
                    </div>
                    <span 
                      className="px-3 py-1 rounded-pill" 
                      style={{ background: "#ffe5e5", color: "#ff4343", fontSize: "12px", fontWeight: "600" }}
                    >
                      Paid
                    </span>
                  </div>
                  <p className="text-blacklight mt-2 x-small italic">
                    * SMS messages are charged based on your current SMS plan.
                  </p>
                </div>
              </CardComp>

              {/* Save Button */}
              <div className="d-flex justify-content-center pt-4">
                <ButtonComp
                  btnText={isUpdating ? "Saving..." : "Save Settings"}
                  btnClassName="bg-primary1 text-white px-5 py-3 rounded-10 border-0 shadow"
                  type="submit"
                  disabled={isUpdating}
                  ButtonInlineStyle={{ minWidth: "250px" }}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default EventSettings;
